import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, ExternalLink, Eye, Link2, Mail, Palette, RefreshCw, ShoppingBag, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const when = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');

const Stat = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-white rounded-lg border border-neutral-200 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <p className="text-2xl font-semibold text-neutral-900 font-mono mt-1">{value}</p>
  </div>
);

// Preview the reminder email and send a test copy before it reaches customers
const ReminderTestTools = ({ token }) => {
  const [html, setHtml] = useState(null);
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const preview = () => axios.get(`${API_URL}/admin/design-reminders/preview?origin=${encodeURIComponent(window.location.origin)}`, auth).then((r) => setHtml(r.data)).catch(() => toast.error('Could not load the preview'));
  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_URL}/admin/design-reminders/test`, { to, origin: window.location.origin }, auth);
      toast.success(`Test reminder sent to ${to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Failed to send the test');
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="reminder-test-tools">
      <Button size="sm" variant="outline" onClick={preview} className="gap-1.5" data-testid="reminder-preview-btn"><Eye className="w-3.5 h-3.5" /> Preview email</Button>
      <form onSubmit={send} className="flex items-center gap-2">
        <Input type="email" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@apolloshades.com" className="h-9 w-60" data-testid="reminder-test-email" />
        <Button size="sm" type="submit" disabled={sending} className="gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="reminder-test-send">
          <Mail className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Send test'}
        </Button>
      </form>
      <Dialog open={!!html} onOpenChange={(o) => !o && setHtml(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="reminder-preview-dialog">
          <DialogHeader className="px-5 pt-4"><DialogTitle>Reminder email preview</DialogTitle></DialogHeader>
          <p className="px-5 -mt-2 text-xs text-neutral-500">Subject: Your Apollo design is saved — pick up where you left off</p>
          <iframe title="Reminder email preview" srcDoc={html || ''} className="w-full h-[70vh] border-t border-neutral-200 bg-white" data-testid="reminder-preview-frame" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Saved-design reminder emails: sent per day and shoppers who came back to order
const DesignReminderStats = ({ token }) => {
  const [r, setR] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/admin/design-reminders/stats?days=30`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setR(res.data)).catch(() => toast.error('Failed to load reminder stats'));
  }, [token]);
  if (!r) return null;
  const s = r.summary;
  const maxSent = Math.max(1, ...r.daily.map((d) => d.sent));
  return (
    <section className="space-y-4 pt-2" data-testid="design-reminder-stats">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-amber-600" /> Saved-design reminders <span className="text-xs font-normal text-neutral-500">· last 30 days</span></h3>
        <p className="text-sm text-neutral-500">One friendly email ~3 days after a shopper emails themselves a design and hasn't ordered (sent daily at 10:00 ET).</p>
        <ReminderTestTools token={token} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Reminders sent" value={s.sent} icon={Mail} testid="reminder-stat-sent" />
        <Stat label="Came back & ordered" value={s.returned} icon={ShoppingBag} testid="reminder-stat-returned" />
        <Stat label="Return rate" value={`${s.return_rate}%`} icon={TrendingUp} testid="reminder-stat-rate" />
        <Stat label="Skipped (ordered / opted out)" value={s.skipped} icon={Eye} testid="reminder-stat-skipped" />
      </div>
      {r.daily.length === 0 ? (
        <p className="text-sm text-neutral-500 bg-white border border-dashed border-neutral-200 rounded-lg p-4 text-center" data-testid="reminder-daily-empty">No reminders sent yet — they start 3 days after shoppers use “Email my design”.</p>
      ) : (
        <div className="bg-white rounded-lg border border-neutral-200 divide-y divide-neutral-100" data-testid="reminder-daily">
          {r.daily.map((d) => (
            <div key={d.date} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`reminder-day-${d.date}`}>
              <span className="w-20 text-neutral-500">{when(d.date)}</span>
              <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${(100 * d.sent) / maxSent}%` }} /></div>
              <span className="w-16 text-right font-mono">{d.sent} sent</span>
              <span className="w-24 text-right font-mono text-emerald-700">{d.returned} ordered</span>
            </div>
          ))}
        </div>
      )}
      {r.returned.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200" data-testid="reminder-returned">
          <p className="px-4 pt-3 text-xs uppercase tracking-wider text-neutral-500">Shoppers who returned to order</p>
          {r.returned.map((x) => (
            <div key={`${x.email}-${x.ordered_at}`} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm border-t border-neutral-100">
              <span className="font-medium">{x.email}</span>
              <span className="text-neutral-500">reminded {when(x.reminded_at)} → ordered {when(x.ordered_at)}</span>
              <span className="font-mono text-emerald-700">{x.order_total != null ? `$${Number(x.order_total).toFixed(2)}` : x.design_total || ''}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// Admin view: which shared design links get opened most, and which fabrics they feature.
export const SharedDesignAnalyticsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/shared-designs/analytics?limit=25`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load shared design analytics');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const copy = (path) => navigator.clipboard.writeText(`${window.location.origin}${path}`).then(() => toast.success('Link copied')).catch(() => toast.error('Copy failed'));

  if (loading && !data) return <p className="text-neutral-500 py-10 text-center">Loading…</p>;
  const s = data?.summary || {};
  const maxFabricLinks = Math.max(1, ...(data?.top_fabrics || []).map((f) => f.links));

  return (
    <div className="space-y-6" data-testid="shared-design-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Shared Design Links</h2>
          <p className="text-sm text-neutral-500">Links shoppers created from the configurator, and how often they're opened.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="analytics-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Links created" value={s.links ?? 0} icon={Link2} testid="analytics-links" />
        <Stat label="Total opens" value={s.total_views ?? 0} icon={Eye} testid="analytics-views" />
        <Stat label="Open rate" value={`${s.open_rate ?? 0}%`} icon={TrendingUp} testid="analytics-open-rate" />
        <Stat label="Avg opens / link" value={s.avg_views_per_link ?? 0} icon={Eye} testid="analytics-avg" />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white rounded-lg border border-neutral-200" data-testid="analytics-top-links">
          <div className="px-5 py-4 border-b border-neutral-200 font-medium flex items-center gap-2"><Link2 className="w-4 h-4 text-amber-600" /> Most opened links</div>
          {(data?.top_links || []).length === 0 ? (
            <p className="text-neutral-500 text-sm text-center py-8">No shared designs yet</p>
          ) : (
            <div className="divide-y divide-neutral-100">
              {data.top_links.map((l) => (
                <div key={l.id} className="px-5 py-3 flex items-center gap-3" data-testid={`analytics-link-${l.id}`}>
                  <div className="w-10 h-10 rounded-md border border-neutral-200 overflow-hidden flex-shrink-0" style={{ backgroundColor: l.fabric?.hex_color || '#f5f5f4' }}>
                    {l.fabric?.image_url && <img src={l.fabric.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{l.fabric?.name || 'Unknown fabric'} <span className="text-neutral-400 font-normal">· {[l.fabric?.brand_name, l.fabric?.collection_name].filter(Boolean).join(' / ')}</span></p>
                    <p className="text-xs text-neutral-500 truncate">{l.product_type} · {l.size || 'no size'} · {l.mount_type || '—'} · {l.control_type || '—'} · {when(l.created_at)}</p>
                  </div>
                  <span className="font-mono text-sm px-2 py-1 rounded bg-amber-50 text-amber-800 flex items-center gap-1" data-testid={`analytics-link-views-${l.id}`}><Eye className="w-3.5 h-3.5" /> {l.views}</span>
                  <button onClick={() => copy(l.path)} className="text-neutral-400 hover:text-neutral-900" title="Copy link" data-testid={`analytics-copy-${l.id}`}><Copy className="w-4 h-4" /></button>
                  <a href={l.path} target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-neutral-900" title="Open"><ExternalLink className="w-4 h-4" /></a>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200" data-testid="analytics-top-fabrics">
          <div className="px-5 py-4 border-b border-neutral-200 font-medium flex items-center gap-2"><Palette className="w-4 h-4 text-amber-600" /> Most shared fabrics</div>
          {(data?.top_fabrics || []).length === 0 ? (
            <p className="text-neutral-500 text-sm text-center py-8">No fabrics yet</p>
          ) : (
            <div className="p-4 space-y-3">
              {data.top_fabrics.map((f) => (
                <div key={f.color_id} data-testid={`analytics-fabric-${f.color_id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium truncate">{f.name} <span className="text-neutral-400 font-normal">· {f.collection_name}</span></span>
                    <span className="text-neutral-500 text-xs flex-shrink-0 ml-2">{f.links} link{f.links === 1 ? '' : 's'} · {f.views} opens</span>
                  </div>
                  <div className="h-2 rounded-full bg-neutral-100 mt-1 overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${(f.links / maxFabricLinks) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
          {(data?.by_product || []).length > 0 && (
            <div className="px-5 py-3 border-t border-neutral-100 text-xs text-neutral-500 flex flex-wrap gap-3">
              {data.by_product.map((p) => <span key={p.product_type}>{p.product_type}: <strong className="text-neutral-800">{p.links}</strong></span>)}
            </div>
          )}
        </div>
      </div>
      <DesignReminderStats token={token} />
    </div>
  );
};

export default SharedDesignAnalyticsTab;
